import React, { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Grid,
  Box,
  Text,
  Flex,
  RadioGroup,
  Radio,
  Stack,
  Alert,
  AlertIcon,
  AlertDescription
} from '@rockr/rock-ui'
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Select
} from '@chakra-ui/core'
import { CustomIcon } from '../../../components/CustomIcon'
import { MoneyInput } from '../../../components/MoneyInput'
import { RootState } from '../../../shared/store'
import { OfferInput, SkuCategories } from '../../../shared/types/skus'
import { brlFormatter } from '../../../shared/helpers/formatters'
import { getTotalValue } from '../../../shared/helpers/offer'
import { editOffer } from '../../../shared/store/offers/actions'
import { DateInput } from '../../../components/DateInput'
import { IntegerInput } from '../../../components/IntegerInput'
import { createGenericTypesService } from '../../../shared/api/generic-types'
import { useFormikContext } from 'formik'
import {formatBooleanInput} from '../../../shared/helpers/input'
import { OfferTag } from '../../../components/OfferTag'

type ProductStream = 'saas' | 'marketplace' | 'services'
type InstallmentFrequency = 'Monthly' | 'Quarterly' | 'Twice a year' | 'Annual'

const useInstallmentFrequencies = () => {
  const [installmentFrequencies, setInstallmentFrequencies] = useState<{
    label: InstallmentFrequency
    value: number | string
  }[]>([])
  const genericTypesService = createGenericTypesService()

  const parseToSelectInputFormat = (data: any) => {
    return { value: data.id, label: data.description }
  }

  useEffect(() => {
    genericTypesService.getInstallmentFrequencies()
      .then(response => response.map(parseToSelectInputFormat))
      .then(data => setInstallmentFrequencies(data))

  // eslint-disable-next-line
  }, [])

  return installmentFrequencies
}

const usePaymentMethods = () => {
  const [paymentMethods, setPaymentMethods] = useState<{
    label: string
    value: number | string
  }[]>([])
  const genericTypesService = createGenericTypesService()

  const parseToSelectInputFormat = (data: any) => {
    return { value: data.id, label: data.description }
  }

  useEffect(() => {
    genericTypesService.getPaymentMethods()
      .then(response => response.map(parseToSelectInputFormat))
      .then(data => setPaymentMethods(data))

  // eslint-disable-next-line
  }, [])

  return paymentMethods
}

export const Finance = () => {
  const [groupedSkus, setGroupedSkus] = useState({
    saas: [],
    marketplace: [],
    services: []
  })
  const installmentFrequencies = useInstallmentFrequencies()
  const paymentMethods = usePaymentMethods()

  const offers = useSelector<RootState, OfferInput[]>(state => state.offers)
  const dispatch = useDispatch()

  const {
    setValues,
    errors,
    submitCount
  } = useFormikContext()

  const getSkuCodeFromOffer = useCallback((offer: OfferInput) => {
    if (offer.bundleId) {
      const mainSku = offer.skus?.find(sku => sku.value > 0)

      if (!mainSku) {
        throw new Error('BundleWithoutValue')
      }

      return mainSku.type?.code ?? mainSku.skuCode
    }

    return offer.skuCode
  }, [])

  const groupSkusByCategory = useCallback(() => {
    return offers.reduce((grouped, offer, index) => {
      if(offer.isChurn) {
        return {
          ...grouped
        }
      }

      const categories = {
        SFW: 'saas',
        RCC: 'marketplace',
        SVC: 'services'
      }

      const skuCode = getSkuCodeFromOffer(offer)
      const category = categories[skuCode as SkuCategories]
      const prevCategoryItens = grouped[category as ProductStream]
      const offerWithIndex = { ...offer, indexTable: index }

      return {
        ...grouped,
        [category]: [...prevCategoryItens, offerWithIndex]
      }
    }, { saas: [], marketplace: [], services: [] })
  }, [offers, getSkuCodeFromOffer])

  useEffect(() => {
    const groupedSkus = groupSkusByCategory()
    setGroupedSkus(groupedSkus)
    setValues(offers)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSkusByCategory, offers])

  const getNumberOfInstallmentsByFrequency = (
    duration: number,
    frequencyId?: number
  ) => {
    const frequency = installmentFrequencies.find(
      frequency => frequency.value === frequencyId
    )

    if (!frequency) {
      return 0
    }

    const durationInYears = Math.ceil(duration / 12)
    const installmentsByFrequencyId = {
      'Monthly': 12 * durationInYears,
      'Quarterly': 4 * durationInYears,
      'Twice a year': 2 * durationInYears,
      'Annual': 1 * durationInYears
    }
    
    return installmentsByFrequencyId[frequency.label]
  }

  const updateInstallment = (
    offerIndex: number,
    payload: {
      installmentProperty: 'installmentPaymentMethodId' | 'firstInstallmentDate' | 'installmentFrequencyId',
      value: any
    }
  ) => {
    const { installmentProperty, value } = payload
    const offer = offers[offerIndex]

    const updatedOffer = {
      ...offer,
      payment: {
        ...offer.payment,
        [installmentProperty]: value
      }
    }

    dispatch(editOffer({
      indexTable: offerIndex,
      data: updatedOffer
    }))
  }

  const updateUpfront = (
    offerIndex: number,
    payload: {
      upfrontProperty: 'hasUpfront' | 'upfrontDate' | 'upfrontValue' | 'upfrontPaymentMethodId',
      value: any
    }
  ) => {
    const { upfrontProperty, value } = payload
    const offer = offers[offerIndex]

    if(offer.payment[upfrontProperty] !== value) {
      const updatedOffer = {
        ...offer,
        payment: {
          ...offer.payment,
          [upfrontProperty]: value
        }
      }
  
      dispatch(editOffer({
        indexTable: offerIndex,
        data: updatedOffer
      }))
    }

  }

  const shouldRenderInstallments = (offer: OfferInput) => {
    const { payment } = offer

    if (payment?.hasUpfront) {
      const upfrontEqualTotal = payment.upfrontValue === getTotalValue(offer)
      return !upfrontEqualTotal
    }

    return true
  }

  const getInstallmentValue = (offer: OfferInput) => {
    if (!offer.payment) {
      return 0
    }

    const { duration, payment } = offer
    const { installmentFrequencyId } = payment
    const total = getTotalValue(offer)
    const upfrontValue = payment?.upfrontValue ?? 0

    const numberOfInstallments = getNumberOfInstallmentsByFrequency(
      duration,
      installmentFrequencyId!
    )

    if (numberOfInstallments === 0) {
      return 0
    }
    const installmentValue = (total - upfrontValue) / numberOfInstallments

    return installmentValue
  }

  const updateHasUpfront = (
    offer: OfferInput & { indexTable: number },
    value: 'yes' | 'no'
  ) => {
    const hasUpfront = value === 'yes'

    updateUpfront(
      offer.indexTable,
      {
        upfrontProperty: 'hasUpfront',
        value: hasUpfront
      }
    )

    if (!hasUpfront) {
      updateUpfront(
        offer.indexTable,
        {
          upfrontProperty: 'upfrontValue',
          value: 0
        }
      )
    }
  }

  const hasErrors = (index: number) => {
    if(submitCount > 0 && errors && Object.keys(errors).includes(String(index))) {
      const messages = (errors as any)[index]
      return (
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>
            {Object.keys(messages).map((idx: any) => (
              <p key={idx}>{messages[idx]}</p>
            ))}
          </AlertDescription>
        </Alert>
      )
    }
    return null
  }

  const formatDate = (value?: Date) => {
    if(value) {
      return new Date(value)
    }
    return undefined
  }

  const offerPaymentIsDisabled = (offer: OfferInput) => {
    return Boolean(offer.id && !offer.isUpdated)
  }

  const getOutstandingBalance = (payment: any, total: number, value: number) => {
    const {
      installmentPaymentMethodId,
      hasUpfront,
      upfrontValue,
      upfrontPaymentMethodId,
      installmentFrequencyId
    } = payment
    
    if(
      (installmentPaymentMethodId && installmentFrequencyId && !hasUpfront) || 
      (installmentPaymentMethodId && installmentFrequencyId && hasUpfront && upfrontValue && upfrontPaymentMethodId)
    ) {
      return 0
    }
    return total - value - (payment?.upfrontValue ?? 0)
  }

  const renderSkuAccordion = (offer: OfferInput & { indexTable: number }) => {
    const total = getTotalValue(offer)
    const value = getInstallmentValue(offer)
    const { duration, payment } = offer
    const numberOfInstallments = getNumberOfInstallmentsByFrequency(
      duration,
      payment?.installmentFrequencyId
    )
    
    const outstandingBalance = getOutstandingBalance(payment, total, value)

    return (
      <AccordionItem paddingY={2}>
        <AccordionButton
          bg={hasErrors(offer.indexTable) ? '#FFC7C7' : undefined}
        >
          <Flex
            alignItems='center'
            justifyContent='space-between'
            w='100%'
          >
            <Text fontWeight='medium' align="left">
              {offer.displayName} {'\u00A0'}
              <OfferTag offer={offer} />
            </Text>
            <Box>
              <Text align='right'>
                Total: {brlFormatter.format(getTotalValue(offer) / 100)}
              </Text>
              <Text fontSize={12} color='#677389' align='right'>
                Outstanding balance: {'\u00A0'}
                <Text fontWeight='bold' display='inline'>
                  {brlFormatter.format(outstandingBalance / 100)}
                </Text>
              </Text>
            </Box>
          </Flex>

          <AccordionIcon />
        </AccordionButton>

        <AccordionPanel>
          {hasErrors(offer.indexTable) && (
            <Grid paddingTop={5}>
              {hasErrors(offer.indexTable)}
            </Grid>
          )}
          <Grid
            gridGap={10}
            paddingTop={5}
            templateColumns='repeat(5, 1fr)'
          >
            <Box>
              <Text marginBottom={5} fontWeight='medium'>
                Upfront payment
              </Text>

              <RadioGroup
                name='hasUpfront'
                value={formatBooleanInput(offer.payment?.hasUpfront)}
                onChange={(value: 'yes' | 'no') => updateHasUpfront(offer, value)}
              >
                <Stack spacing={8} direction='row'>
                  <Radio value='yes' isDisabled={offerPaymentIsDisabled(offer)}>
                    <Text fontSize={18}>
                      Yes
                    </Text>
                  </Radio>

                  <Radio value='no' isDisabled={offerPaymentIsDisabled(offer)}>
                    <Text fontSize={18}>
                      No
                    </Text>
                  </Radio>
                </Stack>
              </RadioGroup>
            </Box>

            {offer.payment?.hasUpfront && (
              <>
                <Box>
                  <Text marginBottom={3} fontWeight='medium'>
                    Upfront payment value
                  </Text>
                  <MoneyInput
                    name='upfrontPaymentValue'
                    currency='BRL'
                    max={getTotalValue(offer)}
                    disabled={offerPaymentIsDisabled(offer)}
                    onChange={e => {
                      const { value } = e.target
                      const upfrontValue = Number(value)
                      updateUpfront(
                        offer.indexTable,
                        {
                          upfrontProperty: 'upfrontValue',
                          value: upfrontValue
                        }
                      )
                    }}
                    value={offer.payment?.upfrontValue ?? 0}
                  />
                </Box>

                <Box>
                  <Text marginBottom={3} fontWeight='medium'>
                    Upfront payment date
                  </Text>
                  <DateInput
                    name='upfrontPaymentDate'
                    value={formatDate(offer.payment.upfrontDate)}
                    disabled={offerPaymentIsDisabled(offer)}
                    onChange={e => {
                      const { value } = e.target
                      updateUpfront(
                        offer.indexTable,
                        {
                          upfrontProperty: 'upfrontDate',
                          value
                        }
                      )
                    }}
                    dateFormat='dd/MM/yyyy'
                  />
                </Box>

                <Box>
                  <Text marginBottom={3} fontWeight='medium'>
                    Upfront payment method
                  </Text>
                  <Select
                    name='upfrontPaymentMethodId'
                    disabled={offerPaymentIsDisabled(offer)}
                    onChange={e => {
                      const { value } = e.target
                      updateUpfront(
                        offer.indexTable,
                        {
                          upfrontProperty: 'upfrontPaymentMethodId',
                          value: Number(value)
                        }
                      )
                    }}
                    value={offer.payment.upfrontPaymentMethodId}
                  >
                    <option>
                      Select...
                    </option>
                    {paymentMethods.map(method => (
                      <option value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </Select>
                </Box>
              </>
            )}
          </Grid>

          {shouldRenderInstallments(offer) && (
            <Grid
              marginTop={10}
              gridGap={10}
              templateColumns='repeat(5, 1fr)'
            >
              <Box>
                <Text marginBottom={3} fontWeight='medium'>
                  Installment Frequency
                </Text>
                <Select
                  name='installmentFrequencyId'
                  disabled={offerPaymentIsDisabled(offer)}
                  onChange={(e: any) => {
                    const { value } = e.target

                    updateInstallment(
                      offer.indexTable,
                      {
                        value: Number(value) || null,
                        installmentProperty: 'installmentFrequencyId'
                      }
                    )
                  }}
                  value={offers[offer.indexTable]?.payment?.installmentFrequencyId}
                >
                  <option>
                    Select...
                  </option>
                  {installmentFrequencies.map(installment => (
                    <option value={installment.value}>
                      {installment.label}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box>
                <Text marginBottom={3} fontWeight='medium'>
                  Number of installments
                </Text>
                <IntegerInput
                  name='numberOfInstallments'
                  value={numberOfInstallments || 0}
                  disabled
                />
              </Box>

              <Box>
                <Text marginBottom={3} fontWeight='medium'>
                  Installment date
                </Text>
                <DateInput
                  name='paymentDate'
                  disabled={offerPaymentIsDisabled(offer)}
                  value={formatDate(offers[offer.indexTable]?.payment?.firstInstallmentDate)}
                  onChange={e => {
                    const { value } = e.target
                    updateInstallment(
                      offer.indexTable,
                      {
                        value,
                        installmentProperty: 'firstInstallmentDate'
                      }
                    )
                  }}
                  dateFormat='dd/MM/yyyy'
                />
              </Box>

              <Box>
                <Text marginBottom={3} fontWeight='medium'>
                  Installment value
                </Text>
                <MoneyInput
                  disabled
                  value={value}
                  name='installmentValue'
                  currency='BRL'
                />
              </Box>

              <Box>
                <Text marginBottom={3} fontWeight='medium'>
                  Payment method
                </Text>
                <Select
                  name='paymentMethodId'
                  disabled={offerPaymentIsDisabled(offer)}
                  onChange={e => {
                    const { value } = e.target
                    updateInstallment(
                      offer.indexTable,
                      {
                        installmentProperty: 'installmentPaymentMethodId',
                        value: Number(value) || null
                      }
                    )
                  }}
                  value={offers[offer.indexTable]?.payment?.installmentPaymentMethodId}
                >
                  <option>
                    Select...
                  </option>
                  {paymentMethods.map(method => (
                    <option value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </Select>
              </Box>
            </Grid>
          )}
        </AccordionPanel>
      </AccordionItem>
    )
  }

  return (
    <section>
      <Grid gridGap={4}>
        <Text marginBottom={6}>Payment terms</Text>

        {groupedSkus.saas.length > 0 && (
          <Box>
            <Text fontWeight='bold' color='#2D3648'>
              <CustomIcon boxSize="45px" name='rock' marginRight={4} />
              SaaS
            </Text>

            <Accordion marginTop={4} allowToggle>
              {groupedSkus.saas.map(renderSkuAccordion)}
            </Accordion>
          </Box>
        )}

        {groupedSkus.services.length > 0 && (
          <Box>
            <Text fontWeight='bold' color='#2D3648'>
              <CustomIcon boxSize="45px" name='tools' marginRight={4} />
              Professional Services
            </Text>

            <Accordion marginTop={4} allowToggle>
              {groupedSkus.services.map(renderSkuAccordion)}
            </Accordion>
          </Box>
        )}

        {groupedSkus.marketplace.length > 0 && (
          <Box>
            <Text fontWeight='bold' color='#2D3648'>
              <CustomIcon boxSize="45px" name='bag' marginRight={4} />
              Marketplace
            </Text>

            <Accordion marginTop={4} allowToggle>
              {groupedSkus.marketplace.map(renderSkuAccordion)}
            </Accordion>
          </Box>
        )}
      </Grid>
    </section>
  )
}
